import { createRouter, createWebHistory } from 'vue-router';
import HomePage from '/src/HomePage.vue';
import TagPage from '/src/TagPage.vue';
import ExportChannelsPage from '/src/ExportChannelsPage.vue';
import FundingPage from '/src/FundingPage.vue';
import i18n from '../i18n';
import { SUPPORTED_LOCALES, detectPreferredLocale } from '../constants/locales';

// 支持的语言列表，用于 URL 前缀和语言切换
const supportedLocales = SUPPORTED_LOCALES;

// 从 URL 路径中提取语言代码，用于 SEO 和路由控制
export function getLocaleFromPath(path) {
    const firstSegment = path.split('/')[1];
    return supportedLocales.includes(firstSegment) ? firstSegment : null;
}

// 为路径添加语言前缀，确保 URL 符合多语言 SEO 规范
export function addLocalePrefix(path, locale) {
    return `/${locale}${path}`;
}

// 基础路由配置，将被转换为带有语言前缀的本地化路由
const baseRoutes = [
    {
        path: '/'
    },
    {
        path: '/tools',
        name: 'tools',
        component: () => import('../ToolsPage.vue')
    },
    {
        path: '/:date?',
        name: 'home',
        component: HomePage
    },
    {
        path: '/tag/:tag(.*)/:date?',
        name: 'tagPage',
        component: TagPage,
        props: true,
        beforeEnter(to, from, next) {
            // 解码标签名（移除可能的日期部分）
            let tagName = decodeURIComponent(to.params.tag);
            // 如果标签中包含日期（刷新页面的情况），提取实际的标签名
            if (tagName.includes('/')) {
                const parts = tagName.split('/');
                tagName = parts[0];
                // 如果没有日期参数但URL中包含日期，设置日期参数
                if (!to.params.date && parts[1]) {
                    to.params.date = parts[1];
                }
            }

            const lowerCaseTag = tagName.toLowerCase();

            // 如果需要规范化标签名
            if (tagName !== lowerCaseTag) {
                const path = `/tag/${encodeURIComponent(lowerCaseTag)}`;
                next({
                    path: to.params.date ? `${path}/${to.params.date}` : path,
                    replace: true
                });
            } else {
                // 更新参数
                to.params.tag = lowerCaseTag;
                next();
            }
        }
    },
    {
        path: '/export-channels',
        name: 'exportChannels',
        component: ExportChannelsPage
    },
    {
        path: '/funding',
        name: 'funding',
        component: FundingPage
    },
    {
        path: '/search/:query?/:date?',
        name: 'search',
        component: () => import('../SearchPage.vue'),
        beforeEnter(to, from, next) {
            // 解码搜索词（移除可能的日期部分）
            let searchQuery = to.params.query ? decodeURIComponent(to.params.query) : '';
            
            // 如果搜索词中包含日期（刷新页面的情况），提取实际的搜索词
            if (searchQuery.includes('/')) {
                const parts = searchQuery.split('/');
                searchQuery = parts[0];
                // 如果没有日期参数但URL中包含日期，设置日期参数
                if (!to.params.date && parts[1]) {
                    to.params.date = parts[1];
                }
            }

            // 更新参数
            to.params.query = searchQuery;
            next();
        }
    }
];

// 通过 flatMap 将基础路由转换为带有语言前缀的多语言路由配置
const localizedRoutes = supportedLocales.flatMap(locale => {
    return baseRoutes.map(route => {
        const localizedRoute = { ...route };
        
        // 处理根路径的特殊情况，为每种语言创建独立的首页路由
        if (route.path === '/') {
            localizedRoute.path = `/${locale}`;
            localizedRoute.name = `home-${locale}`;
            delete localizedRoute.redirect;
            localizedRoute.component = HomePage;
            return localizedRoute;
        }

        // 为所有路由添加语言前缀，确保 URL 结构符合 SEO 要求
        localizedRoute.path = addLocalePrefix(route.path, locale);
        if (localizedRoute.name) {
            localizedRoute.name = `${localizedRoute.name}-${locale}`;
        }
        return localizedRoute;
    });
});

// 创建路由实例，包含根路由重定向和本地化路由
const router = createRouter({
    history: createWebHistory(),
    // 根路径 '/' 的语言重定向完全由全局守卫 appRouteGuard 处理（见下方 beforeEach）：
    // 爬虫 → /en、普通用户 → 首选语言。原行内 beforeEnter 与之重复且矛盾（行内写 /zh），
    // 且因全局守卫先执行而永远跑不到，属死代码，已移除。
    routes: [
        ...localizedRoutes
    ],
    // 切换路由后始终回到页面顶部：修复 product→funding 等页面切换时
    // 窗口滚动高度被继承、未重置的问题（默认不配置则浏览器保留原滚动位置）。
    scrollBehavior() {
        return { top: 0 };
    }
});

// 获取用户的语言偏好，按优先级：localStorage > 浏览器设置 > 默认语言
// 实现收敛到 constants/locales.js 的 detectPreferredLocale，避免与 LanguageSwitcher 重复
export function getUserPreferredLanguage() {
    return detectPreferredLocale();
}

// 全局路由守卫：处理语言重定向和 i18n 设置
export function appRouteGuard(to, from, next) {
    // 如果路径看起来像是静态资源（包含 . 扩展名），不要做语言前缀重定向，直接放行
    if (/\.[^/]+$/.test(to.path)) {
        next();
        return;
    }
    const pathLocale = getLocaleFromPath(to.path);
    const preferredLanguage = getUserPreferredLanguage();
    
    // 检查是否来自搜索引擎爬虫
    const userAgent = navigator.userAgent.toLowerCase();
    const isBot = /bot|googlebot|crawler|spider|robot|crawling/i.test(userAgent);
    
    // 仅对根路径进行语言重定向
    if (to.path === '/') {
        if (isBot) {
            // 如果是爬虫，默认展示中文版本
            next('/en');
        } else {
            // 用户访问根路径时重定向到首选语言
            next(`/${preferredLanguage}`);
        }
        return;
    }
    
    // 如果URL中没有语言前缀且不是根路径，添加默认语言前缀
    if (!pathLocale && to.path !== '/') {
        const targetPath = addLocalePrefix(to.path, 'zh');
        next({ path: targetPath, replace: true });
        return;
    }
    
    // 设置i18n语言为URL中指定的语言
    if (pathLocale && i18n.global.locale.value !== pathLocale) {
        i18n.global.locale.value = pathLocale;
    }

    // 设置页面标题
    const routeName = to.name?.split('-')[0]; // 获取基础路由名称（去除语言后缀）
    if (routeName) {
        let title = '';
        const websiteName = i18n.global.t('interface.ai_inspiration_hub');
        
        // 根据路由名称获取对应的标题
        if (routeName === 'search' && to.params.query) {
            title = i18n.global.t('interface.title.search', { query: to.params.query });
        } else if (routeName === 'tagPage' && to.params.tag) {
            title = i18n.global.t('interface.title.tag', { tag: decodeURIComponent(to.params.tag) });
        } else {
            title = i18n.global.t(`interface.title.${routeName}`);
        }
        
        // 设置最终的页面标题
        // 对于首页，不在路由守卫中设置标题，让组件自己管理
        if (routeName !== 'home') {
            document.title = `${title} - ${websiteName}`;
        }
    }
    
    next();
}

router.beforeEach(appRouteGuard);

export default router;
